import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';

/**
 * CHARGEMENT DIFFÉRÉ DES PAGES DE L'APP.
 *
 * Jusqu'au 02/09/2026 un seul bundle portait tout : le Bureau, le dossier,
 * le Poste Juriste, les deux portails avocat, la console interne, la
 * cinématique — 1,07 Mo gzip téléchargés par un visiteur venu lire une
 * landing. Chaque page ci-dessous devient son propre chunk, chargé au
 * premier passage sur sa route.
 *
 * Les pages PUBLIQUES restent importées en dur, et c'est voulu : elles sont
 * PRÉ-RENDUES au build (scripts/prerender.mjs) et HYDRATÉES au chargement
 * (src/index.js). Une frontière Suspense au-dessus d'une page hydratée
 * casserait l'hydratation (le DOM pré-rendu ne porte pas de marqueur de
 * frontière). `lazyPage` pose donc sa frontière DANS l'élément de route,
 * qui n'existe que quand cette route est active.
 */
const lazyPage = (loader) => {
  const Inner = lazy(loader);
  const Page = (props) => (
    <Suspense fallback={<div data-testid="page-loading" style={{ minHeight: '60vh' }} />}>
      <Inner {...props} />
    </Suspense>
  );
  return Page;
};
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useFeatureFlags, usePublicConfigLoaded } from './hooks/usePublicConfig';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
const Layout = lazyPage(() => import('./components/Layout'));
import { LANDING_SLUGS } from './content/landings';
// Pages publiques : chunks séparés, hydratables (cf. publicRoutes.js).
const {
  HomeV3, TeamV3, PlansV2, TeamsV3, LandingThemePage, GuidesHub, HowItWorksV2,
  Blog, BlogArticle, BePillarPage, EnginePage, LegalPage, AttorneyApply,
} = PUBLIC_PAGES;
import { REDIRECTS } from './lib/seo/metadata';
import { PAGES_DETRESSE_SLUGS } from './content/pagesDetresse';
import { PUBLIC_PAGES } from './publicRoutes';
const CreditHistoryPage = lazyPage(() => import('./pages/CreditHistoryPage'));
const Login = lazyPage(() => import('./pages/Login'));
const Signup = lazyPage(() => import('./pages/Signup'));
// Le dépôt sans inscription — la porte du produit. Publique, sans
// ProtectedRoute : c'est tout son objet (cf. pages/IntakePage.jsx).
const IntakePage = lazyPage(() => import('./pages/IntakePage'));
const ResetPassword = lazyPage(() => import('./pages/ResetPassword'));
const BureauV2 = lazyPage(() => import('./pages/BureauV2'));
const Cases = lazyPage(() => import('./pages/Cases'));
const CaseDetailV7 = lazyPage(() => import('./pages/CaseDetailV7'));
// LA PAGE DOSSIER V7 (30/09/2026) remplace la V3 derrière le même
// drapeau. `pages/CaseDetailV3.jsx` reste dans le dépôt, non routé.
const DossierV7 = lazyPage(() => import('./pages/DossierV7'));
const JuristCallBooking = lazyPage(() => import('./pages/JuristCallBooking'));
const CaseClosure = lazyPage(() => import('./pages/CaseClosure'));
const BureauV3 = lazyPage(() => import('./pages/BureauV3'));
const Upload = lazyPage(() => import('./pages/Upload'));
const AttorneysDirectory = lazyPage(() => import('./pages/AttorneysDirectory'));
const MonJuriste = lazyPage(() => import('./pages/MonJuriste'));
const Settings = lazyPage(() => import('./pages/Settings'));
const PaymentSuccess = lazyPage(() => import('./pages/PaymentSuccess'));
const PostPayScreen = lazyPage(() => import('./pages/PostPayScreen'));
const PaymentCancel = lazyPage(() => import('./pages/PaymentCancel'));
const LegalChat = lazyPage(() => import('./pages/LegalChat'));
const SharedCase = lazyPage(() => import('./pages/SharedCase'));
const AnalyzeDocument = lazyPage(() => import('./pages/AnalyzeDocument'));
const ContractGuard = lazyPage(() => import('./pages/ContractGuard'));
const MobileScan = lazyPage(() => import('./pages/MobileScan'));
import FloatingChatButton from './components/FloatingChatButton';
const PublicAttorneyProfile = lazyPage(() => import('./pages/PublicAttorneyProfile'));
const AdminAttorneys = lazyPage(() => import('./pages/AdminAttorneys'));
const AdminMatchingDashboard = lazyPage(() => import('./pages/Admin/MatchingDashboard'));
const DocumentViewer = lazyPage(() => import('./pages/DocumentViewer'));
const VideoCall = lazyPage(() => import('./pages/VideoCall'));
// Cinématique d'analyse. V6 est pilotée par les événements réels du
// dossier et montre le vrai document ; V5 reste dans le repo, plus
// routée — même traitement que HomePage et MyDesk avant elle.
const CinematiqueV6 = lazyPage(() => import('./components/CinematiqueV6/CinematiqueV6'));
// Sprint A — Attorney Portal (parallel to legacy /attorney/*)
const AttorneyPortalJoin = lazyPage(() => import('./pages/Attorneys/AttorneyJoin'));
const AttorneyPortalLogin = lazyPage(() => import('./pages/Attorneys/AttorneyLogin'));
const AttorneyPortalMagicVerify = lazyPage(() => import('./pages/Attorneys/AttorneyMagicVerify'));
const AttorneyPortalLiveCounsel = lazyPage(() => import('./pages/Attorneys/LiveCounsel'));
const AttorneyPortalProfile = lazyPage(() => import('./pages/Attorneys/Profile'));
import RequireAttorneyAuth from './components/Attorneys/RequireAttorneyAuth';
// Internal admin dashboard
const InternalAdminLogin = lazyPage(() => import('./pages/Internal/AdminLogin'));
const InternalAdminLayout = lazyPage(() => import('./components/Internal/AdminLayout'));
const InternalAdminDashboard = lazyPage(() => import('./pages/Internal/AdminDashboard'));
const InternalAdminAttorneys = lazyPage(() => import('./pages/Internal/AdminAttorneys'));
const InternalAdminCustomers = lazyPage(() => import('./pages/Internal/AdminCustomers'));
const InternalAdminCases = lazyPage(() => import('./pages/Internal/AdminCases'));
const InternalAdminJuristCases = lazyPage(() => import('./pages/Internal/AdminJuristCases'));
const InternalAdminLetterQueue = lazyPage(() => import('./pages/Internal/AdminLetterQueue'));
const InternalAdminAttorneyAppointments = lazyPage(() => import('./pages/Internal/AdminAttorneyAppointments'));
const InternalAdminOperations = lazyPage(() => import('./pages/Internal/AdminOperations'));
const InternalAdminModeration = lazyPage(() => import('./pages/Internal/AdminModeration'));
const InternalAdminAnalytics = lazyPage(() => import('./pages/Internal/AdminAnalytics'));
const InternalAdminSettings = lazyPage(() => import('./pages/Internal/AdminSettings'));
const InternalAdminB2BSignals = lazyPage(() => import('./pages/Internal/AdminB2BSignals'));
const InternalAdminPromoCodes = lazyPage(() => import('./pages/Internal/AdminPromoCodes'));
// Le Poste Juriste — l'OS du juriste augmenté. Même session que l'admin
// (jeton `admin_token`), rôle vérifié serveur sur chaque route.
const PosteLayout = lazyPage(() => import('./pages/Jurist/PosteLayout'));
// LA FILE (phase A, 08/09/2026) : le portefeuille en grille. L'ancien
// écran carte par carte (`PosteFile`) reste servi sur /jurist/deck : c'est
// toujours le circuit d'approbation des lettres et des relances.
const PostePortfolio = lazyPage(() => import('./pages/Jurist/PostePortfolio'));
const PosteFile = lazyPage(() => import('./pages/Jurist/PosteFile'));
const PosteMyCases = lazyPage(() => import('./pages/Jurist/PosteMyCases'));
const PosteLetters = lazyPage(() => import('./pages/Jurist/PosteLetters'));
const PosteCorrections = lazyPage(() => import('./pages/Jurist/PosteCorrections'));
const PosteAgenda = lazyPage(() => import('./pages/Jurist/PosteAgendaSemaine'));
const PostePlaybooks = lazyPage(() => import('./pages/Jurist/PostePlaybooks'));
const PosteLettre = lazyPage(() => import('./pages/Jurist/PosteLettre'));
const PosteActivite = lazyPage(() => import('./pages/Jurist/PosteActivite'));
const PosteSupervision = lazyPage(() => import('./pages/Jurist/PosteSupervision'));
const JuristMessages = lazyPage(() => import('./pages/Jurist/JuristMessages'));
const JuristArbitrate = lazyPage(() => import('./pages/Jurist/JuristArbitrate'));
const JuristTower = lazyPage(() => import('./pages/Jurist/JuristTower'));
// La vue Dossier de la phase A : trois zones, playbook, références (08/09/2026).
const PosteDossier = lazyPage(() => import('./pages/Jurist/PosteDossier'));
// SEO pages
// La 404 du site. Montée sur le joker `*`, tout en bas de la table.
const NotFound = lazyPage(() => import('./pages/NotFound'));
// Pages légales — un composant, quatre routes (cf. pages/LegalPage.jsx).
import { LEGAL_SLUGS } from './content/legal';
// Bandeau de consentement + module qui garde les traceurs sous clé.
import CookieBanner from './components/CookieBanner';
import { initTrackers } from './lib/consent';
// Contact WhatsApp des pages publiques — un lien wa.me, derrière
// SHOW_WHATSAPP_CONTACT. Se filtre lui-même (cf. WhatsAppFab).
import WhatsAppFab from './components/WhatsAppFab';
// Toast feedback — must be mounted once at root so useToast() from anywhere in the app is visible.
import { Toaster } from './components/ui/toaster';
import { captureAttribution } from './utils/attribution';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Keep-alive: ping backend every 4 minutes to prevent cold starts
const useKeepAlive = () => {
  useEffect(() => {
    const ping = () => fetch(`${BACKEND_URL}/api/health`).catch(() => {});
    ping();
    const id = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
};

/**
 * LA PAGE DOSSIER, ET SON INTERRUPTEUR.
 *
 * UNE SEULE ROUTE, DEUX RENDUS. `/cases/:caseId` ne se dédouble pas :
 * les liens du produit, les retours Stripe (`?overage=`,
 * `?letter_oneshot=`) et les tests existants continuent de viser la
 * même URL. Revenir en arrière, c'est repasser `ENABLE_CASE_PAGE_V3`
 * à `false` — il n'y a rien à défaire.
 *
 * ON ATTEND LA RÉPONSE AVANT DE CHOISIR. Les drapeaux valent `false`
 * par défaut pendant le fetch : rendre tout de suite afficherait la V2,
 * puis la remplacerait par la V3 quatre-vingts millisecondes plus tard,
 * sous les yeux du client. Le squelette ci-dessous dure le temps de la
 * requête, et se confond avec l'écran de chargement des deux pages.
 */
const CaseDetailRoute = () => {
  const { caseId } = useParams();
  const { case_page_v3_enabled: v3Enabled } = useFeatureFlags();
  const configLoaded = usePublicConfigLoaded();

  // UNE REVUE DE CONTRAT N'A PAS DE DOSSIER VIVANT.
  // Un `cg_…` n'existe pas dans `db.cases` : il vit dans
  // `db.contract_guard_reviews`, et `GET /cases/{id}` en synthétise un
  // Case pour la page d'analyse. Le Dossier V3, lui, appelle
  // `GET /cases/{id}/live`, dont la garde cherche dans `db.cases` — donc
  // 404, donc écran d'erreur. Le produit ne pointe plus ici (Contract
  // Guard vise `/analyse`), mais les liens déjà en circulation, eux,
  // pointent encore. On les rattrape.
  //
  // AVANT le drapeau, pas après : la redirection ne dépend d'aucune
  // configuration, et attendre l'aurait fait clignoter.
  if ((caseId || '').startsWith('cg_')) {
    return <Navigate to={`/cases/${caseId}/analyse`} replace />;
  }

  if (!configLoaded) {
    return (
      <div
        data-testid="case-detail-flag-pending"
        style={{
          minHeight: '60vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#f4f4f1',
        }}
      />
    );
  }
  return v3Enabled ? <DossierV7 /> : <CaseDetailV7 />;
};

/**
 * LE BUREAU, ET SON INTERRUPTEUR.
 *
 * `ENABLE_DASHBOARD_V3` change TROIS choses ensemble : `/dashboard` rend
 * le bureau V3, `/cases` y redirige (voir `CasesRoute` juste dessous), et
 * la barre latérale perd « Mes dossiers » et sa mini-liste. Les trois
 * vont de pair — laisser une entrée de navigation vers une page qui
 * redirige apprend au client que la navigation ment.
 *
 * Même attente de configuration que `CaseDetailRoute` : les drapeaux
 * valent `false` pendant le fetch, et rendre tout de suite montrerait
 * l'ancien bureau avant de le remplacer sous les yeux du client.
 */
const DashboardRoute = () => {
  const { dashboard_v3_enabled: v3Enabled } = useFeatureFlags();
  const configLoaded = usePublicConfigLoaded();

  if (!configLoaded) {
    return (
      <div
        data-testid="dashboard-flag-pending"
        style={{
          minHeight: '60vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#f4f4f1',
        }}
      />
    );
  }
  return v3Enabled ? <BureauV3 /> : <BureauV2 />;
};

/**
 * `/cases` — la page existe encore, mais elle n'a plus lieu d'être quand
 * le bureau porte déjà la liste complète et ses filtres.
 *
 * REDIRECTION, PAS SUPPRESSION : l'URL est dans des favoris, dans des
 * emails, et dans l'historique des navigateurs. Elle continue de mener
 * quelque part de sensé au lieu de tomber sur un 404.
 */
const CasesRoute = () => {
  const { dashboard_v3_enabled: v3Enabled } = useFeatureFlags();
  const configLoaded = usePublicConfigLoaded();

  if (!configLoaded) return null;
  return v3Enabled ? <Navigate to="/dashboard" replace /> : <Cases />;
};

// Attorney route guard
const AttorneyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.account_type !== 'attorney') return <Navigate to="/dashboard" replace />;
  return children;
};

// LA BRANCHE `#session_id=` A ÉTÉ SUPPRIMÉE LE 19/08/2026.
//
// Elle court-circuitait TOUT le routeur — aucune route ne se rendait
// tant qu'un fragment `#session_id=` traînait dans l'URL — pour laisser
// `AuthCallback` échanger le jeton du courtier Emergent, dont le point
// d'échange rend 404 depuis le déménagement. Le nouveau flux Google
// (`/api/auth/google/callback`) ouvre la session côté serveur et
// redirige vers une vraie URL de l'app, cookie posé : il n'y a plus rien
// à intercepter ici.
const AppRouter = () => {
  return (
    <Routes>
      {/* Public routes */}
      {/* Home v3 — iso-pixel avec design-refs/home-v3. HomeV2 et l'ancienne
          HomePage restent dans le repo (plus routées). */}
      <Route path="/" element={<HomeV3 />} />
      {/* legacy Archer Protect, redirigé le 11/07. La page elle-même est
          supprimée : elle portait encore les paliers US ($149, $89.99) et
          « Suivi avocat dédié », c'est-à-dire un modèle de prix et un
          vocabulaire que le produit n'a plus. La redirection reste, pour
          les liens qui pointeraient encore dessus. */}
      <Route path="/legacy-landing" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      {/* Le dépôt sans inscription. `/signup` reste en place et intact :
          les deux portes coexistent, et `ENABLE_ANON_INTAKE` décide
          seulement laquelle les BOUTONS du site désignent. */}
      <Route path="/analyser" element={<IntakePage />} />
      {/* Réinitialisation de mot de passe. La même page porte la demande
          (sans token) et le choix du nouveau mot de passe (avec token) —
          voir l'en-tête de ResetPassword. */}
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/shared/:token" element={<SharedCase />} />
      <Route path="/attorney/apply" element={<AttorneyApply />} />
      {/* Mobile-only: landing page for the QR-scan cross-device upload flow.
          Desktop creates a token, shows a QR; phone opens /m/scan/:token, uploads. */}
      <Route path="/m/scan/:token" element={<MobileScan />} />

      {/* Pages légales — publiques, accessibles sans connexion, indexables.
          Les quatre routes sont écrites depuis le registre
          (content/legal/index.js) : ajouter un document là-bas suffit. */}
      {LEGAL_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<LegalPage slug={slug} />} />
      ))}

      {/* SEO: Blog + Pillar pages + State pages */}
      <Route path="/guides" element={<GuidesHub />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      {/* Les quatre piliers AMÉRICAINS (/eviction-help, /wrongful-termination,
          /severance-negotiation, /ai-legal-assistant) et leurs sous-pages par
          État ont été SUPPRIMÉS le 17/08/2026, avec `pages/PillarPage`,
          `content/pillarPages` et `content/statePages`.

          Ils étaient publics ET déclarés au sitemap, donc soumis à Google —
          sur un produit belge dont le marché US est gelé jusqu'à M6. Leur
          contenu violait trois règles à lui seul : « an attorney-signed
          response letter, it costs $49 and is delivered in 4 hours » cumulait
          un prix en dollars, un délai chiffré promis et la facturation d'un
          acte d'avocat au client — produit supprimé le 13/08. S'y ajoutaient
          « 2.4M+ case laws » (le produit en annonce 300 000+), « 70% of severance
          packages are negotiable » et « accuracy exceeds 90% », tous sans
          source, et repris en rich snippet via le JSON-LD FAQ.

          La coquille était anglaise en dur alors que le sélecteur de langue
          FR/NL/EN restait actif au-dessus, sans effet. */}

      {/* SEO — Belgian FR pillar pages (chantier seo-content §14.2).
          Each route is fixed so the sitemap / hreflang / canonical
          stay predictable; BePillarPage looks up the slug from the
          pathname internally. */}
      <Route path="/contester-amende-radar"     element={<BePillarPage />} />
      <Route path="/lettre-huissier-que-faire"  element={<BePillarPage />} />
      <Route path="/caution-locative-non-rendue" element={<BePillarPage />} />
      <Route path="/facture-abusive-contester"  element={<BePillarPage />} />
      {/* Pages de détresse, en relecture (content/pagesDetresse.js) :
          routées pour la relecture, noindex tant qu'elles ne sont pas
          signées. */}
      {PAGES_DETRESSE_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<BePillarPage />} />
      ))}

      {/* Les 16 landings thématiques de campagne (/pv, /telecom, …).
          Pages publiques, hors menu principal : on y arrive par une pub,
          un lien social ou Google, jamais par la nav. Un seul composant,
          une entrée de contenu par thème (content/landings.js). */}
      {LANDING_SLUGS.map((slug) => (
        <Route
          key={slug}
          path={`/${slug}`}
          element={REDIRECTS[`/${slug}`]
            ? <Navigate to={REDIRECTS[`/${slug}`]} replace />
            : <LandingThemePage />}
        />
      ))}

      {/* Sprint A — Attorney Portal (must be declared BEFORE /attorneys/:slug) */}
      {/* « Qui se bat pour toi » remplace l'ancienne page Avocats sur la
          même route. L'ancienne page reste dans le repo, plus routée. */}
      <Route path="/attorneys" element={<TeamV3 />} />
      {/* Alias : la maquette et la nav parlent d'équipe, pas d'avocats. */}
      <Route path="/ton-equipe" element={<Navigate to="/attorneys" replace />} />
      <Route path="/qui-se-bat-pour-toi" element={<Navigate to="/attorneys" replace />} />
      <Route path="/engine" element={<EnginePage />} />
      {/* Tarifs v2 — transposition de la référence design validée.
          Les deux anciennes pages Tarifs (PricingPlansPage, Pricing) ont
          été supprimées avec la grille v2 : gardées hors routage, elles
          portaient encore des prix morts prêts à être recopiés. */}
      <Route path="/plans" element={<PlansV2 />} />
      {/* Archer for Teams v3 — page publique B2B, accessible sans login
          (design-refs/for-teams-v3). TeamsPage reste dans le dépôt, non
          routée : revenir en arrière = remettre son import ici et dans
          publicRoutes.js. */}
      <Route path="/entreprises" element={<TeamsV3 />} />
      <Route path="/account/credits" element={<ProtectedRoute><CreditHistoryPage /></ProtectedRoute>} />
      <Route path="/attorneys/join" element={<AttorneyPortalJoin />} />
      <Route path="/attorneys/login" element={<AttorneyPortalLogin />} />
      <Route path="/attorneys/login/verify" element={<AttorneyPortalMagicVerify />} />
      {/* LE RÔLE AVOCAT DU POSTE (phase A, ④) : le portail des dossiers
          (tableau de bord, desk, inbox, mes dossiers, terminés, détail)
          redirige vers le Poste. Le Live Counsel et le profil restent
          (phase C). /attorneys/join, /login et /attorney/apply restent publics. */}
      <Route path="/attorneys/dashboard" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/desk" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/inbox" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/my-cases" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/completed" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/cases/:assignmentId" element={<Navigate to="/jurist/file" replace />} />
      <Route path="/attorneys/live-counsel" element={<RequireAttorneyAuth><AttorneyPortalLiveCounsel /></RequireAttorneyAuth>} />
      <Route path="/attorneys/profile" element={<RequireAttorneyAuth><AttorneyPortalProfile /></RequireAttorneyAuth>} />

      <Route path="/attorneys/:slug" element={<PublicAttorneyProfile />} />
      {/* legacy Archer Protect, redirigé le 11/07 — /pricing served the
          old US Protect Solo/Family/Pro cards (€89.99, etc.), retired
          when we aligned on the BP tiers. Both the plain path and the
          locale-prefixed variant redirect to the current /plans page. */}
      <Route path="/pricing" element={<Navigate to="/plans" replace />} />
      <Route path="/:lang/pricing" element={<Navigate to="/plans" replace />} />
      {/* « Affaires gagnées » a été SUPPRIMÉE, page et composant
          compris : elle présentait des témoignages nominatifs inventés
          sous le titre « Des vraies victoires », et des agrégats
          (1,24 M€, 89 %) sans source. Elle sera refaite sur les données
          réelles du traceur (case_outcomes).

          La route reste, en redirection vers la home — même traitement
          que /legacy-landing et /pricing. Sans elle, un lien externe ou
          un favori tomberait sur le joker de fin de table, qui renvoie
          vers /dashboard, donc vers /login pour un visiteur non
          connecté : une page publique qui devient un écran de connexion.
          Un lien qui a existé ne doit pas se refermer sur une demande
          d'identifiants. */}
      <Route path="/winning-cases" element={<Navigate to="/" replace />} />
      {/* Nouvelle page « Comment ça marche ». L'ancienne reste dans le
          repo, plus routée, comme MyDesk avant elle. */}
      <Route path="/how-it-works" element={<HowItWorksV2 />} />
      <Route path="/:lang/how-it-works" element={<HowItWorksV2 />} />
      <Route path="/admin/attorneys" element={<ProtectedRoute><AdminAttorneys /></ProtectedRoute>} />
      <Route path="/admin/matching" element={<ProtectedRoute><AdminMatchingDashboard /></ProtectedRoute>} />

      {/* Internal admin dashboard (new auth system) */}
      <Route path="/internal/dashboard-x9k7/login" element={<InternalAdminLogin />} />
      <Route path="/internal/dashboard-x9k7" element={<InternalAdminLayout />}>
        <Route index element={<InternalAdminDashboard />} />
        <Route path="attorneys" element={<InternalAdminAttorneys />} />
        <Route path="customers" element={<InternalAdminCustomers />} />
        <Route path="cases" element={<InternalAdminCases />} />
        <Route path="jurist-cases" element={<InternalAdminJuristCases />} />
        <Route path="letter-queue" element={<InternalAdminLetterQueue />} />
        {/* Demandes de rendez-vous avocat — file manuelle, aucune
            attribution automatique derrière. */}
        <Route path="attorney-appointments" element={<InternalAdminAttorneyAppointments />} />
        <Route path="operations" element={<InternalAdminOperations />} />
        <Route path="moderation" element={<InternalAdminModeration />} />
        <Route path="analytics" element={<InternalAdminAnalytics />} />
        <Route path="b2b-signals" element={<InternalAdminB2BSignals />} />
        {/* Codes promo. Aucune garde React : la page interroge le serveur,
            qui répond 403 à tout rôle autre que super_admin — un `admin`
            qui tape l'URL voit le refus, pas le module. */}
        <Route path="promo-codes" element={<InternalAdminPromoCodes />} />
        <Route path="settings" element={<InternalAdminSettings />} />
      </Route>

      {/* LE POSTE JURISTE. Pas de garde React ici : `PosteLayout` demande
          au SERVEUR qui il a en face (`/api/jurist-desk/me`) et affiche le
          refus s'il n'a pas le rôle. Un rôle lu côté navigateur se change
          dans la console du navigateur — celui-ci est vérifié à chaque
          appel d'API, pas une fois au montage.

          `LegacyScreen` enveloppe les vues que la refonte design ne
          couvre pas encore : elles vivent sous `juristDesk.css`, scopée
          sous `.jd`, et perdraient TOUS leurs styles sans cette racine.
          Le jour où l'une d'elles est refaite, on retire son enveloppe —
          et elle seule. */}
      <Route path="/jurist" element={<PosteLayout />}>
        <Route index element={<Navigate to="/jurist/file" replace />} />
        <Route path="file" element={<PostePortfolio />} />
        <Route path="deck" element={<PosteFile />} />
        {/* Le portefeuille — tous les dossiers portés par le juriste
            connecté, quand la File est le plan du jour. */}
        <Route path="dossiers" element={<PosteMyCases />} />
        <Route path="messages" element={<JuristMessages />} />
        {/* La file d'envoi, dans la langue du Poste. Ce N'EST PLUS le
            composant de la console : celui-là est en anglais, monté sous
            une autre coquille, avec ses styles en ligne — le traduire et
            le restyler l'aurait emmené avec lui dans /internal. Deux
            écrans, mais UNE SEULE API (`/api/admin/letter-sends`) et les
            mêmes gardes serveur. */}
        <Route path="lettres" element={<PosteLetters />} />
        {/* LE CORPUS DE LA BOUCLE DE CAPTURE. Accessible à tout rôle du
            Poste et non aux seuls admins : le juriste qui signale doit
            pouvoir relire ce qu'il a signalé, et voir qu'un collègue a
            déjà relevé la même chose. Le PÉRIMÈTRE, lui, est posé côté
            serveur — un compte de démonstration ne voit que le corpus de
            démonstration. */}
        <Route path="corrections" element={<PosteCorrections />} />
        {/* L'AGENDA — les disponibilités déclarées, et les appels pris
            dessus. Il n'est PAS derrière ENABLE_JURIST_BOOKING : un
            juriste doit pouvoir remplir son agenda avant que le premier
            client puisse y réserver (cf. l'en-tête de
            routes/jurist_agenda_routes.py). */}
        <Route path="agenda" element={<PosteAgenda />} />
        <Route path="arbitrer" element={<JuristArbitrate />} />
        <Route path="tour" element={<JuristTower />} />
        {/* « Supervision » — les agrégats du temps mesuré. Distinct de
            « La Tour », qui compte des dossiers et des décisions : les
            deux écrans répondent à deux questions différentes et
            coexistent. */}
        <Route path="supervision" element={<PosteSupervision />} />
        <Route path="activite" element={<PosteActivite />} />
        {/* La bibliothèque des playbooks et l'éditeur de lettre (phase A, ③). */}
        <Route path="playbooks" element={<PostePlaybooks />} />
        <Route path="lettre/:caseId" element={<PosteLettre />} />
        <Route path="dossier/:caseId" element={<PosteDossier />} />
      </Route>

      <Route path="/documents/:documentId" element={<ProtectedRoute><DocumentViewer /></ProtectedRoute>} />
      {/* Cinématique v5 — arc émotionnel, données réelles égrenées.
          L'ancienne CinematiqueV4 reste dans le repo (plus routée). */}
      <Route path="/analyze/:caseId" element={<ProtectedRoute><CinematiqueV6 /></ProtectedRoute>} />
      
      {/* Attorney protected routes */}
      {/* L'ancien portail /attorney (comptes clients `account_type: attorney`)
          redirige vers la connexion des avocats partenaires, puis le Poste.
          Seul /attorney/apply reste public (déclaré plus haut). */}
      <Route path="/attorney" element={<Navigate to="/attorneys/login" replace />} />
      <Route path="/attorney/*" element={<Navigate to="/attorneys/login" replace />} />

      {/* Video call (both attorney and client) */}
      <Route path="/video-call/:callId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
      <Route path="/attorney-call-confirmed" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

      {/* ÉCRAN POST-PAIEMENT — hors `Layout`, et c'est le dessin.

          C'est un MOMENT, pas un lieu : la maquette n'a ni barre
          latérale, ni en-tête, ni fil d'ariane, une seule carte centrée
          et une seule action. La coquille lui donnerait une navigation à
          quitter alors qu'elle propose déjà la seule chose à faire.

          La route existe TOUJOURS, drapeau ouvert ou fermé. C'est
          `ENABLE_POSTPAY_SCREEN`, lu côté serveur à la création du
          Checkout, qui décide si Stripe y envoie quelqu'un ; fermé, la
          page se contente de rediriger vers le bureau. Une route qui
          n'existerait que sous condition ferait une 404 à tout client
          encore en train de payer au moment où on referme le drapeau. */}
      <Route path="/apres-paiement" element={<ProtectedRoute><PostPayScreen /></ProtectedRoute>} />

      {/* Client protected routes with Layout */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Bureau v2 — transposition de la référence design validée,
            branchée sur les données réelles. L'ancien MyDesk reste dans
            le repo (plus routé). */}
        <Route path="dashboard" element={<DashboardRoute />} />
        <Route path="cases" element={<CasesRoute />} />
        <Route path="cases/:caseId" element={<CaseDetailRoute />} />
        {/* L'ANALYSE COMPLÈTE, SUR SON URL PROPRE.

            `CaseDetailV7` n'est pas une ancienne page dossier : c'est LA
            page d'analyse du produit — les cinq étapes du parcours,
            « 01 · Ta situation », le score, les constats, la stratégie —
            celle où le client atterrit après chaque scan depuis le début.

            Tant qu'elle partageait `/cases/:caseId` avec la V3, ouvrir
            `ENABLE_CASE_PAGE_V3` la rendait INATTEIGNABLE. Et avec elle
            partaient l'envoi de lettre (`SendLetterOptions`,
            `generate-action-letter`, `letter/send` — dont c'est le seul
            appelant client), « Explique-moi simplement », l'affinage du
            dossier, « marquer comme résolu » et les bandeaux de
            juridiction, d'avocat et d'échéance. Aucun n'a d'autre porte.

            Elle a donc sa route, SANS drapeau : les deux pages
            coexistent, chacune la sienne. `/cases/:caseId` reste
            l'arbitrage de `CaseDetailRoute` — le Dossier vivant V3 quand
            le drapeau est ouvert, cette même page quand il est fermé,
            ce qui est la position de repli et non une seconde adresse.

            React Router classe le segment statique `analyse` au-dessus
            du paramètre : l'ordre de déclaration ici n'a aucun effet. */}
        <Route path="cases/:caseId/analyse" element={<CaseDetailV7 />} />
        {/* LA CLÔTURE, SUR SON URL PROPRE.

            La dernière page du cycle de vie : le client dit comment ça
            s'est terminé, puis voit son résultat tamponné. Elle a une
            adresse parce que trois choses y pointent — le bandeau
            « issue non confirmée » du Dossier vivant, le bouton de fin
            de colonne, et « marquer comme résolu » sur la page
            d'analyse. Une modale n'aurait pas eu d'adresse à leur
            donner.

            La route existe SANS drapeau, comme `/apres-paiement` : c'est
            la PAGE qui lit `ENABLE_CASE_CLOSURE` et renvoie au dossier
            quand il est fermé. Une route conditionnelle ferait une 404 à
            tout client déjà en train de clôturer au moment où on le
            referme.

            React Router classe le segment statique `cloture` au-dessus du
            paramètre : l'ordre de déclaration ici n'a aucun effet. */}
        {/* RÉSERVER UN APPEL — une PAGE et non une modale : sept
            colonnes de créneaux demandent de la place, et l'achat d'un
            appel supplémentaire doit se faire sans cacher la grille (on
            ne demande pas d'acheter à l'aveugle). La page se referme
            elle-même vers le dossier si ENABLE_JURIST_BOOKING est fermé —
            le serveur répond 404 de son côté, les deux disent la même
            chose. */}
        <Route path="cases/:caseId/appel" element={<JuristCallBooking />} />
        <Route path="cases/:caseId/cloture" element={<CaseClosure />} />
        <Route path="documents" element={<AnalyzeDocument />} />
        <Route path="upload" element={<Upload />} />
        {/* Le foyer du pilier humain : toutes les conversations juriste
            du client, tous dossiers confondus. Elle remplace l'annuaire
            avocats DANS LA BARRE LATÉRALE — la route /lawyers, elle,
            reste : le marketplace n'a pas disparu du produit, il n'est
            plus qu'atteint depuis le dossier qui le justifie. */}
        <Route path="mon-juriste" element={<MonJuriste />} />
        {/* « Parle à un avocat » — annuaire data-driven. L'ancienne page
            Lawyers a été SUPPRIMÉE le 12/08/2026, pas seulement dépubliée :
            elle était le dernier appelant de `GET /api/lawyers`, qui servait
            17 fiches inventées à qui les demandait. Garder le fichier hors
            routage avait laissé l'API ouverte huit jours. */}
        {/* /lawyers/book a été supprimée : elle lisait `db.lawyers` quand
            l'annuaire lit `db.attorneys`, aucun identifiant de l'annuaire
            n'y résolvait, et son `catch` la renvoyait sur /lawyers. Elle
            était inatteignable, et l'aurait-elle été qu'elle rebondissait.
            La demande de rendez-vous se fait maintenant depuis la carte. */}
        <Route path="lawyers" element={<AttorneysDirectory />} />
        <Route path="contract-guard" element={<ContractGuard />} />
        {/* « /contracts » rendait la page « Cette page n'existe pas. ».
            C'est le pluriel naturel du menu (« Mes contrats », « Vérifier
            un contrat »), donc une URL que le client tape ou garde en
            favori. Redirection, pas 404 — comme /cases vers le bureau. */}
        <Route path="contracts" element={<Navigate to="/contract-guard" replace />} />
        <Route path="settings" element={<Settings />} />
        <Route path="chat" element={<LegalChat />} />
        <Route path="payment/success" element={<PaymentSuccess />} />
        <Route path="payment/cancel" element={<PaymentCancel />} />
      </Route>
      
      {/* Joker — une VRAIE 404 depuis le 17/08/2026.
          Il renvoyait vers /dashboard, donc vers /login pour un visiteur :
          quiconque suivait un lien périmé croyait qu'Archer lui réclamait
          ses identifiants, sans jamais apprendre qu'il s'était trompé
          d'adresse. Mesuré en production sur quatorze chemins morts, tous
          arrivés sur le même écran de connexion.
          La page reste servie en HTTP 200 — `serve_spa` rend index.html
          pour tout chemin inconnu — mais elle porte `noindex` et elle DIT
          ce qui se passe. Cf. pages/NotFound.jsx. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  // Acquisition attribution: snapshot the utm tags / referrer / landing
  // page of the FIRST page of the visit. Runs once, before any in-app
  // navigation can rewrite the URL, and is replayed by Signup with the
  // registration call. See utils/attribution.js.
  useEffect(() => { captureAttribution(); }, []);

  // Traceurs soumis au consentement. `initTrackers` ne CHARGE rien : il
  // déclare les pixels auprès de lib/consent.js, qui ne les exécutera que
  // si le visiteur a explicitement accepté (et jamais avant). Sans
  // identifiant configuré, il n'y a même rien à déclarer.
  useEffect(() => { initTrackers(); }, []);

  return (
    <BrowserRouter>
      {/* Le scroll est réglé UNE fois, ici, pour toutes les routes —
          publiques comme connectées. Posé en frère d'`AppRouter` et non
          dedans : `AppRouter` court-circuite tout le routage sur le retour
          OAuth (`#session_id=`), et le scroll doit rester géré même là. */}
      <ScrollToTop />
      <AuthProvider>
        <AppRouter />
        {/* Le bandeau décide lui-même de ne pas se montrer dans l'app
            connectée (cf. isPublicSiteRoute). Monté ici, en frère du
            routeur, il survit aux navigations sans se remonter. */}
        <CookieBanner />
        {/* Même traitement que le bandeau : monté ici, il décide seul de
            son périmètre (pages publiques uniquement) et ne se remonte
            pas à chaque navigation. Il attend que le bandeau ait obtenu
            sa réponse — ils partagent le même coin de l'écran. */}
        <WhatsAppFab />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
